import axios from "axios";
import {axiosCatchHandler} from "../sharedGlobal";

const SET_LOADING = 'SET_LOADING';
const SET_SILENT_LOADING = 'SET_SILENT_LOADING';
const SET_UNTRACKED_KEYWORDS = 'SET_UNTRACKED_KEYWORDS';
const SET_COMPETITOR_RANKING = 'SET_COMPETITOR_RANKING';
const SET_ADJUSTMENTS = 'SET_ADJUSTMENTS';
const SET_SORT = 'SET_SORT';
const SET_SEARCH = 'SET_SEARCH';
const SET_DATES = 'SET_DATES';
const SET_DATE_FROM = 'SET_DATE_FROM';
const SET_DATE_TO = 'SET_DATE_TO';

const state = {
  loading: false,
  silentLoading: false,
  untrackedKeywords: [],
  meta: [],
  dates: {},
  nextPageUrl: null,
  filters: {
    sortBy: 'position',
    sortDirection: 'asc',
    search: '',
    dateFrom: null,
    dateTo: null,
  },
  competitorRanking: null,
  adjustments: [],
};

const getters = {
  loading: ({loading}) => loading,
  silentLoading: ({silentLoading}) => silentLoading,
  untrackedKeywords: ({untrackedKeywords}) => untrackedKeywords,
  competitorRanking: ({competitorRanking}) => competitorRanking,
  dates: ({dates}) => dates,
  sortBy: ({filters}) => filters.sortBy,
  sortDirection: ({filters}) => filters.sortDirection,
  dateFrom: ({filters}) => filters.dateFrom,
  dateTo: ({filters}) => filters.dateTo,
  hasMorePages: ({nextPageUrl}) => nextPageUrl,
  filters: ({filters}) => filters,
  adjustments: ({adjustments}) => adjustments,
};

const actions = {
  fetchKeywords: async ({commit}, options) => {
    const {fetchFreshData = false, silentLoading = false} = options || {};

    silentLoading ? commit(SET_SILENT_LOADING, true) : commit(SET_LOADING, true);

    const {
      sortBy,
      sortDirection,
      search,
      dateFrom,
      dateTo,
    } = state.filters;

    const url = state.nextPageUrl !== null && !fetchFreshData
      ? state.nextPageUrl
      : '/ajax/keyword-scan';

    try {
      const {data} = await axios.get(url, {
        params: {
          sortBy,
          sortDirection,
          search,
          dateFrom,
          dateTo,
        }
      });

      const keywordData = data.untrackedKeywords;
      commit(SET_UNTRACKED_KEYWORDS, {keywordData, fetchFreshData});
      commit(SET_COMPETITOR_RANKING, data.competitorRanking);
      commit(SET_ADJUSTMENTS, data.adjustments);
      commit(SET_DATES, data.dates);
      commit(SET_LOADING, false);
      commit(SET_SILENT_LOADING, false);
    } catch (error) {
      axiosCatchHandler(error, () => {
      });
      commit(SET_LOADING, false);
      commit(SET_SILENT_LOADING, false);
    }
  },
  addKeyword: async ({commit, dispatch}, keyword) => {
    commit(SET_LOADING, true);

    try {
      await axios.post('/ajax/keywords/update-keywords', {
        new_keywords: [keyword],
      });

      await dispatch('fetchKeywords', {
        fetchFreshData: true,
      });
      commit(SET_LOADING, false);
    } catch (error) {
      axiosCatchHandler(error, () => {
      });
      commit(SET_LOADING, false);
    }
  },
  sortBy: async ({commit, dispatch}, sort) => {
    commit(SET_SORT, sort);
    dispatch('fetchKeywords', {
      fetchFreshData: true,
    });
  },
  search: async ({commit, dispatch}, search) => {
    commit(SET_SEARCH, search);
    dispatch('fetchKeywords', {
      fetchFreshData: true,
    });
  },
  setDateFrom: async ({commit}, dateFrom) => {
    commit(SET_DATE_FROM, dateFrom);
  },
  setDateTo: async ({commit}, dateTo) => {
    commit(SET_DATE_TO, dateTo);
  },
};

const mutations = {
  [SET_LOADING](state, loading) {
    state.loading = loading;
  },
  [SET_SILENT_LOADING](state, loading) {
    state.silentLoading = loading;
  },
  [SET_UNTRACKED_KEYWORDS](state, {keywordData, fetchFreshData}) {
    const {next_page_url: nextPageUrl, data: keywords} = keywordData;
    state.nextPageUrl = nextPageUrl;

    if (fetchFreshData) {
      state.untrackedKeywords = [
        ...keywords
      ];
      return;
    }

    const existentKeywords = state.untrackedKeywords;
    state.untrackedKeywords = [
      ...existentKeywords,
      ...keywords,
    ];
  },
  [SET_COMPETITOR_RANKING](state, competitorRanking) {
    state.competitorRanking = competitorRanking;
  },
  [SET_ADJUSTMENTS](state, adjustments) {
    state.adjustments = adjustments;
  },
  [SET_SORT](state, sort) {
    if (state.filters.sortBy === sort) {
      state.filters.sortDirection = state.filters.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }
    state.filters.sortBy = sort;
    state.filters.sortDirection = sort === 'keyword' ? 'asc' : 'desc';
  },
  [SET_SEARCH](state, search) {
    state.filters.search = search;
  },
  [SET_DATES](state, dates) {
    state.dates = dates;
    if (state.filters.dateFrom === null) {
      state.filters.dateFrom = dates[0].value;
    }
  },
  [SET_DATE_FROM](state, dateFrom) {
    state.filters.dateFrom = dateFrom;
  },
  [SET_DATE_TO](state, dateTo) {
    state.filters.dateTo = dateTo;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
